import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import * as timeBasedWorkPropositionActions from '../../../store/actions/time-based-work-proposition.actions';
import { MessageService } from 'primeng/api';
import { TimeBasedWorkProposition } from '../../../models/time-based-work-proposition.model';

import {
  selectTimeBasedWorkProposition,
  selectTimeBasedWorkPropositionError,
  selectTimeBasedWorkPropositionLoading,
} from '../../../store/selectors/time-based-work-proposition.selector';

@Component({
  selector: 'app-operations-maintenance',
  templateUrl: './operations-maintenance.component.html',
  styleUrl: './operations-maintenance.component.scss',
  standalone: false,
})
export class OperationsMaintenanceComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() ruleUid: string | undefined = undefined;

  operationsMaintenance: TimeBasedWorkProposition[] | undefined = undefined;
  private destroy$ = new Subject<void>();
  loading$ = this.store.select(selectTimeBasedWorkPropositionLoading);
  error$ = this.store.select(selectTimeBasedWorkPropositionError);

  // Modal properties
  showEditModal = false;

  get sortedOperationsMaintenance(): TimeBasedWorkProposition[] {
    if (!this.operationsMaintenance) {
      return [];
    }

    return [...this.operationsMaintenance].sort((a, b) => {
      return (a.description || '').localeCompare(b.description || '');
    });
  }

  constructor(
    private store: Store,
    private messageService: MessageService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ruleUid']) {
      const newRuleUid = changes['ruleUid'].currentValue;
      if (newRuleUid) {
        this.ruleUid = newRuleUid;
        this.loadInitialData();
      }
    }
  }

  ngOnInit(): void {
    this.subscribeToStore();
    if (this.ruleUid) {
      this.loadInitialData();
    }
  }

  private loadInitialData(): void {
    this.store.dispatch(
      timeBasedWorkPropositionActions.loadTimeBasedWorkProposition({
        ruleUid: this.ruleUid,
      }),
    );
  }

  private subscribeToStore(): void {
    combineLatest([this.store.select(selectTimeBasedWorkProposition)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([operationsMaintenance]) => {
        this.operationsMaintenance = operationsMaintenance;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openEditModal(): void {
    this.showEditModal = true;
  }

  onModalSubmit(result: any): void {
    this.handleModalResult(result);
    this.showEditModal = false;
  }

  onModalCancel(): void {
    this.showEditModal = false;
  }

  private async handleModalResult(result: any): Promise<void> {
    const { toCreate, toUpdate, toDelete } = result;

    // Dispatch actions to the store
    if (toDelete && toDelete.length > 0) {
      this.store.dispatch(
        timeBasedWorkPropositionActions.deleteMultipleTimeBasedWorkPropositions(
          {
            uids: toDelete,
          },
        ),
      );
    }

    if (toUpdate && toUpdate.length > 0) {
      const updateRequests = toUpdate.map((operation: any) => ({
        uid: operation.uid,
        description: operation.description,
        periodicity: operation.periodicity,
        ruleFK: this.ruleUid!,
      }));

      this.store.dispatch(
        timeBasedWorkPropositionActions.updateMultipleTimeBasedWorkPropositions(
          {
            requests: updateRequests,
          },
        ),
      );
    }

    if (toCreate && toCreate.length > 0) {
      const createRequests = toCreate.map((operation: any) => ({
        description: operation.description,
        periodicity: operation.periodicity,
        ruleFK: this.ruleUid!,
      }));

      this.store.dispatch(
        timeBasedWorkPropositionActions.createMultipleTimeBasedWorkPropositions(
          {
            requests: createRequests,
          },
        ),
      );
    }
  }
}
